import useShopifySubscription from 'hooks/billing/useShopifySubscription';
import {
    AppBillingInterval,
    AppInstallationSubscription,
    AppRecurringPricingDetails,
} from 'hooks/shopify/queries/get-all-app-subscriptions.graphql';
import { SubscriptionStatus } from 'models/SubscriptionStatus';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import {
    selectShopifyAllSubscriptions,
    selectShopifyAppHandle,
    selectShopifyAppPlans,
    selectShopifyAppPlansFetching,
    selectShopifySubscriptionFetching,
} from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { Addon } from '../../../pb/subscription/v1/addon_pb';
import { ShopifyPlan } from '../../../pb/subscription/v1/shopify_plan_pb';

interface CurrentSubscriptionHelper {
    appHandle: string | undefined;
    currentSubscription: AppInstallationSubscription | undefined;
    /** Every subscription of the app installation, current and past, newest first */
    allSubscriptions: AppInstallationSubscription[];
    isLoading: boolean;
    isLegacy: boolean;
    /** Whether the current plan is one of the legacy Shopify App Pricing plans (its handle contains `legacy-`) */
    isShopifyAppPricingLegacy: boolean;
    isCancelled: boolean;
    /** Whether the current plan is usage-based, from the plan details' `usageBased` */
    isUsageBased: boolean;
    addons: Addon[];
    planHandle: string | undefined;
    /** Every Shopify pricing plan of the app */
    plans: ShopifyPlan[];
    goToPLans: () => void;
    /** Opens one plan's page in the Shopify admin, e.g. to subscribe to it; monthly billing by default */
    goToPlan: (handle: string, interval?: AppBillingInterval) => void;
    goToBilling: () => void;
}

const useCurrentSubscriptionHelper = (): CurrentSubscriptionHelper => {
    const shopifyPlans = useAppSelector(selectShopifyAppPlans);
    const appHandle = useAppSelector(selectShopifyAppHandle);
    const plansFetching = useAppSelector(selectShopifyAppPlansFetching);
    const subscriptionFetching = useAppSelector(selectShopifySubscriptionFetching);
    const { currentSubscription } = useShopifySubscription();
    const allSubscriptions = useAppSelector(selectShopifyAllSubscriptions);
    const isLoading = useMemo(() => plansFetching || subscriptionFetching, [plansFetching, subscriptionFetching]);

    const planHandle = useMemo(() => {
        return (currentSubscription?.lineItems?.[0]?.plan?.pricingDetails as AppRecurringPricingDetails)?.planHandle;
    }, [currentSubscription]);

    const currentPlanDetails = useMemo(() => {
        return shopifyPlans?.plans.find((p) => p.handle === planHandle);
    }, [planHandle, shopifyPlans]);

    const isLegacy = useMemo(() => {
        return !(currentSubscription?.lineItems?.[0]?.plan?.pricingDetails as AppRecurringPricingDetails)?.planHandle;
    }, [currentSubscription]);

    const isShopifyAppPricingLegacy = useMemo(() => !!planHandle?.includes('legacy-'), [planHandle]);

    const isCancelled = useMemo(() => {
        return currentSubscription?.status === SubscriptionStatus.CANCELLED;
    }, [currentSubscription]);

    const isUsageBased = useMemo(() => !!currentPlanDetails?.usageBased, [currentPlanDetails]);

    const addons = useMemo(() => {
        return currentPlanDetails?.addons || [];
    }, [currentPlanDetails]);

    const plans = useMemo(() => shopifyPlans?.plans ?? [], [shopifyPlans]);

    const goToPLans = useCallback(
        () => open(`shopify://admin/charges/${appHandle}/pricing_plans`, '_top'),
        [appHandle]
    );

    const goToPlan = useCallback(
        (handle: string, interval: AppBillingInterval = 'EVERY_30_DAYS') =>
            open(`shopify://admin/charges/${appHandle}/plans/${handle}?${new URLSearchParams({ interval })}`, '_top'),
        [appHandle]
    );

    const goToBilling = useCallback(
        () => open(`shopify://admin/settings/apps/app_installations/app/${appHandle}`, '_top'),
        [appHandle]
    );

    return {
        appHandle,
        currentSubscription,
        allSubscriptions,
        isLoading,
        isLegacy,
        isShopifyAppPricingLegacy,
        isCancelled,
        isUsageBased,
        addons,
        planHandle,
        plans,
        goToPLans,
        goToPlan,
        goToBilling,
    };
};

export default useCurrentSubscriptionHelper;
